// Screen 3, Diary. A feed grouped by date, plus a month calendar that marks
// the days with entries and jumps the feed to that day. Entries still sitting
// in the outbox are shown inline with a "waiting to sync" note, so nothing a
// traveller wrote ever looks lost.
//
// Round 7 made the calendar a working surface rather than a read only mark
// up of the month. Selecting a day offers two real actions: open that day in
// the diary when it has entries, and start a trip on that day. Creating a
// trip here calls the EXISTING POST /trips with the date prefilled, it does
// NOT invent a parallel planning model, which is why the new trip turns up
// in Trips, Itinerary, Budget and Stats immediately: they all read the same
// trips list.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Icon,
  IconBook,
  IconChevronLeft,
  IconChevronRight,
  IconPin,
  IconPlus,
  IconRoute,
  IconVideo,
} from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { Segmented } from "../components/Segmented";
import { apiClient } from "../lib/api";
import {
  dayKey,
  dayKeyOf,
  formatDateShort,
  formatDayHeading,
  formatMonthTitle,
  formatTime,
  monthGrid,
  weekdayLabels,
} from "../lib/format";
import type { Route } from "../lib/router";
import type { CalendarDay, Moment } from "../lib/types";
import { useApp } from "../state/app";

interface FeedEntry {
  key: string;
  caption: string;
  placeName: string | null;
  capturedAt: string;
  queued: boolean;
  media: Array<{ key: string; url: string; isVideo: boolean }>;
}

function fromMoment(moment: Moment): FeedEntry {
  return {
    key: moment.id,
    caption: moment.caption,
    placeName: moment.placeName,
    capturedAt: moment.capturedAt,
    queued: false,
    media: moment.media.map((item) => ({
      key: item.id,
      url: apiClient.mediaUrl(item.storageKey),
      isVideo: item.mime.startsWith("video/"),
    })),
  };
}

/**
 * `route.param` picks the view, so the Kalender shortcut on Beranda and in
 * Profil is a real deep link (#/diary/calendar) rather than a screen the
 * traveller lands on and then has to switch by hand.
 */
export function DiaryScreen({ route }: { route?: Route }) {
  const { t, locale, moments, pendingMoments, activeTripId, createTrip, setActiveTripId } = useApp();
  const [view, setView] = useState<"feed" | "calendar">(
    route?.param === "calendar" ? "calendar" : "feed",
  );
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [tripTitle, setTripTitle] = useState("");
  const [tripForm, setTripForm] = useState(false);
  const [tripState, setTripState] = useState<"idle" | "saving" | "created" | "failed">("idle");
  const [monthDays, setMonthDays] = useState<CalendarDay[] | null>(null);
  const objectUrls = useRef<string[]>([]);

  // Object URLs for queued blobs are created once per render pass and
  // revoked on the next one, so a long diary session does not leak memory.
  const pendingEntries = useMemo<FeedEntry[]>(() => {
    for (const url of objectUrls.current) URL.revokeObjectURL(url);
    objectUrls.current = [];
    return pendingMoments
      .filter((item) => item.tripId === activeTripId)
      .map((item) => ({
        key: item.clientId,
        caption: item.payload.caption,
        placeName: item.payload.placeName,
        capturedAt: item.payload.capturedAt,
        queued: true,
        media: item.payload.media.map((media, index) => {
          if (media.storageKey) {
            return {
              key: `${item.clientId}-${index}`,
              url: apiClient.mediaUrl(media.storageKey),
              isVideo: media.mime.startsWith("video/"),
            };
          }
          const url = media.blob ? URL.createObjectURL(media.blob) : "";
          if (url) objectUrls.current.push(url);
          return { key: `${item.clientId}-${index}`, url, isVideo: media.mime.startsWith("video/") };
        }),
      }));
  }, [pendingMoments, activeTripId]);

  useEffect(
    () => () => {
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
      objectUrls.current = [];
    },
    [],
  );

  const groups = useMemo(() => {
    const all = [...pendingEntries, ...moments.map(fromMoment)].sort((a, b) =>
      b.capturedAt.localeCompare(a.capturedAt),
    );
    const map = new Map<string, FeedEntry[]>();
    for (const entry of all) {
      const key = dayKey(entry.capturedAt);
      const bucket = map.get(key);
      if (bucket) bucket.push(entry);
      else map.set(key, [entry]);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [moments, pendingEntries]);

  const localDays = useMemo(() => new Set(groups.map(([key]) => key)), [groups]);

  /**
   * GET /calendar is a read model over EVERY trip, not just the active one,
   * so it marks days this device has not cached. It is a view, never a store:
   * the same trips and the same moments, read by date. On 404 the calendar
   * falls back to the days it can see locally and nothing looks broken.
   */
  useEffect(() => {
    if (view !== "calendar") return;
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const pad = (n: number) => `${n}`.padStart(2, "0");
    const from = `${year}-${pad(month + 1)}-01`;
    const to = `${year}-${pad(month + 1)}-${pad(new Date(year, month + 1, 0).getDate())}`;
    let live = true;
    void (async () => {
      try {
        const answer = await apiClient.calendar(from, to);
        if (live) setMonthDays(answer?.days ?? null);
      } catch {
        if (live) setMonthDays(null);
      }
    })();
    return () => {
      live = false;
    };
  }, [view, cursor]);

  const daysWithEntries = useMemo(() => {
    const keys = new Set(localDays);
    for (const day of monthDays ?? []) {
      if (day.entryCount > 0) keys.add(day.date);
    }
    return keys;
  }, [localDays, monthDays]);

  /** Days the server says a trip runs through, marked even with no entries. */
  const daysWithTrips = useMemo(
    () => new Set((monthDays ?? []).filter((day) => day.tripIds.length > 0).map((day) => day.date)),
    [monthDays],
  );

  async function createTripOn(day: string): Promise<void> {
    const title = tripTitle.trim();
    if (!title) return;
    setTripState("saving");
    try {
      // The EXISTING trips route, with the chosen day prefilled. One model,
      // so every other screen sees this trip the moment it exists.
      const trip = await createTrip({ title: title.slice(0, 120), startDate: day });
      setActiveTripId(trip.id);
      setTripTitle("");
      setTripForm(false);
      setTripState("created");
    } catch {
      setTripState("failed");
    }
  }

  function jumpToDay(key: string): void {
    setView("feed");
    // Wait one frame for the feed to mount before scrolling to the heading.
    requestAnimationFrame(() => {
      document.getElementById(`day-${key}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }

  return (
    <div className="screen-pad">
      <Segmented<"feed" | "calendar">
        label={t("diary.title")}
        value={view}
        onChange={setView}
        options={[
          { value: "feed", label: t("diary.viewFeed") },
          { value: "calendar", label: t("diary.viewCalendar") },
        ]}
      />

      {view === "calendar" ? (
        <section className="calendar">
          <div className="calendar-head">
            <button
              type="button"
              className="btn-icon"
              aria-label={t("diary.prevMonth")}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            >
              <Icon size={18}>
                <IconChevronLeft />
              </Icon>
            </button>
            <h2 className="calendar-title">
              {formatMonthTitle(cursor.getFullYear(), cursor.getMonth(), locale)}
            </h2>
            <button
              type="button"
              className="btn-icon"
              aria-label={t("diary.nextMonth")}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            >
              <Icon size={18}>
                <IconChevronRight />
              </Icon>
            </button>
          </div>

          <div className="calendar-grid calendar-weekdays">
            {weekdayLabels(locale).map((label) => (
              <span key={label} className="calendar-weekday">
                {label}
              </span>
            ))}
          </div>

          <div className="calendar-grid">
            {monthGrid(cursor.getFullYear(), cursor.getMonth()).map((date, index) => {
              if (!date) return <span key={`pad-${index}`} className="calendar-cell calendar-pad" />;
              const key = dayKeyOf(date);
              const has = daysWithEntries.has(key);
              const onTrip = daysWithTrips.has(key);
              const picked = key === selectedDay;
              const classes = ["calendar-cell"];
              if (has) classes.push("calendar-cell-has");
              else if (onTrip) classes.push("calendar-cell-trip");
              if (picked) classes.push("calendar-cell-picked");
              return (
                <button
                  key={key}
                  type="button"
                  className={classes.join(" ")}
                  aria-pressed={picked}
                  aria-label={
                    has ? `${formatDayHeading(key, locale)}, ${t("calendar.hasEntries")}` : formatDayHeading(key, locale)
                  }
                  onClick={() => {
                    setSelectedDay(key);
                    setTripForm(false);
                    setTripState("idle");
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {selectedDay ? (
            <div className="day-actions">
              <p className="day-actions-title">{formatDayHeading(selectedDay, locale)}</p>
              <p className="field-note">
                {daysWithEntries.has(selectedDay)
                  ? `${groups.find(([key]) => key === selectedDay)?.[1].length ?? 0} ${t("diary.entryCount")}`
                  : t("calendar.noEntries")}
              </p>
              <div className="row-actions">
                {localDays.has(selectedDay) ? (
                  <button
                    type="button"
                    className="btn btn-quiet"
                    onClick={() => jumpToDay(selectedDay)}
                  >
                    <Icon size={18}>
                      <IconBook />
                    </Icon>
                    {t("calendar.jump")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() => {
                    setTripForm((open) => !open);
                    setTripState("idle");
                  }}
                >
                  <Icon size={18}>
                    <IconPlus />
                  </Icon>
                  {t("calendar.newTrip")}
                </button>
              </div>

              {tripForm ? (
                <div className="plan-form">
                  <label className="field">
                    <span className="field-label">{t("trips.fieldTitle")}</span>
                    <input
                      className="input"
                      value={tripTitle}
                      maxLength={120}
                      placeholder={t("trips.fieldTitlePlaceholder")}
                      onChange={(event) => setTripTitle(event.target.value)}
                    />
                  </label>
                  <p className="field-note">
                    {t("trips.fieldStart")}
                    {": "}
                    {formatDateShort(selectedDay, locale)}
                  </p>
                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={tripState === "saving" || !tripTitle.trim()}
                      onClick={() => void createTripOn(selectedDay)}
                    >
                      <Icon size={18} color="var(--color-primary-contrast)">
                        <IconRoute />
                      </Icon>
                      {t("trips.save")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-quiet"
                      onClick={() => setTripForm(false)}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              ) : null}

              {tripState === "created" ? (
                <p className="field-note" role="status">
                  {t("calendar.created")}
                </p>
              ) : null}
              {tripState === "failed" ? (
                <p className="field-note" role="status">
                  {t("calendar.createFailed")}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="field-note">{t("calendar.pickDay")}</p>
          )}
        </section>
      ) : groups.length === 0 ? (
        <EmptyState glyph={<IconBook />} title={t("diary.empty")} hint={t("diary.emptyHint")} />
      ) : (
        <div className="feed">
          {groups.map(([key, entries]) => (
            <section key={key} className="feed-day" id={`day-${key}`}>
              <div className="feed-day-head">
                <h2 className="feed-day-title">{formatDayHeading(key, locale)}</h2>
                <span className="feed-day-count">
                  {entries.length}{" "}
                  {entries.length === 1 ? t("diary.entryCountOne") : t("diary.entryCount")}
                </span>
              </div>

              {entries.map((entry) => (
                <article key={entry.key} className="entry">
                  {entry.media.length > 0 ? (
                    <div className="entry-media">
                      {entry.media.map((media) => (
                        <div key={media.key} className="entry-thumb">
                          {media.isVideo ? (
                            <>
                              <video className="entry-thumb-media" src={media.url} muted playsInline />
                              <span className="entry-thumb-tag">
                                <Icon size={14} color="var(--color-primary-contrast)">
                                  <IconVideo />
                                </Icon>
                              </span>
                            </>
                          ) : (
                            <img className="entry-thumb-media" src={media.url} alt="" />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <p className="entry-caption">{entry.caption || t("diary.noCaption")}</p>

                  <div className="entry-meta">
                    <span>{formatTime(entry.capturedAt, locale)}</span>
                    {entry.placeName ? (
                      <span className="entry-place">
                        <Icon size={14} color="var(--color-text-muted)">
                          <IconPin />
                        </Icon>
                        {entry.placeName}
                      </span>
                    ) : null}
                    {entry.queued ? <span className="entry-queued">{t("diary.queued")}</span> : null}
                  </div>
                </article>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
